import React, { FC, useEffect } from 'react';
import { IoChevronForward, IoInformationCircle } from 'react-icons/io5';

import { Box, Collapse, Group, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { LocalStorageService } from 'services/LocalStorageService';

const CollapsibleSectionRoot: FC<{
    storageKey?: string;
    title: React.ReactNode | ((collapsed: boolean) => React.ReactNode);
    initialOpened?: boolean;
    children: React.ReactNode;
}> = ({ storageKey, title, children, initialOpened = true }) => {
    const [opened, handlers] = useDisclosure(initialOpened);

    useEffect(() => {
        if (!storageKey) return;

        const collapsableSections = LocalStorageService.get('collapsable-sections') || {};

        const savedCollapsed = collapsableSections[storageKey];

        if (savedCollapsed === true) {
            handlers.close();
        }

        if (savedCollapsed === false) {
            handlers.open();
        }
    }, []);

    const toggleCollapsed = () => {
        handlers.toggle();

        if (!storageKey) return;

        const collapsableSections = LocalStorageService.get('collapsable-sections') || {};

        collapsableSections[storageKey] = opened;
        LocalStorageService.store('collapsable-sections', collapsableSections);
    };

    return (
        <Box>
            <UnstyledButton
                component={Group}
                gap={4}
                onClick={toggleCollapsed}
                style={{
                    userSelect: 'none',
                }}
            >
                <IoChevronForward
                    size={10}
                    style={{
                        color: 'var(--mantine-color-gray-6)',
                        transform: opened ? 'rotate(90deg)' : 'rotate(0)',
                        transition: 'transform 0.2s ease',
                    }}
                />
                {typeof title === 'function' ? title(opened) : title}
            </UnstyledButton>

            <Collapse expanded={opened} mt="xs" transitionTimingFunction="ease-in-out">
                {children}
            </Collapse>
        </Box>
    );
};

const Title: FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text fz="xs" fw={500} c="gray.6">
        {children}
    </Text>
);

const RowLabel: FC<{ label: string; tooltip?: string }> = ({ label, tooltip }) => (
    <Group gap={4} wrap="nowrap">
        <Text fz={12} fw={500}>
            {label}
        </Text>
        {tooltip && (
            <Tooltip label={tooltip} multiline w={260} withArrow>
                <span style={{ display: 'inline-flex' }}>
                    <IoInformationCircle size={12} color="var(--mantine-color-gray-5)" />
                </span>
            </Tooltip>
        )}
    </Group>
);

const Row: FC<{ label: string; tooltip?: string; children: React.ReactNode }> = ({ label, tooltip, children }) => (
    <Group wrap="nowrap" gap={4} align="center">
        <Box style={{ width: 140, flexShrink: 0 }}>
            <RowLabel label={label} tooltip={tooltip} />
        </Box>
        <Box style={{ flexGrow: 1, flexShrink: 1 }}>{children}</Box>
    </Group>
);

export const CollapsibleSection = Object.assign(CollapsibleSectionRoot, { Title, Row });
